import React, { useState, useEffect, useCallback } from "react";

interface RetryImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Número máximo de reintentos */
  maxRetries?: number;
  /** Milisegundos entre cada intento */
  retryDelay?: number;
  /** Texto a mostrar mientras carga */
  loadingText?: string;
}

const RetryImage: React.FC<RetryImageProps> = ({
  src,
  alt,
  maxRetries = 5,
  retryDelay = 3000,
  loadingText = "cargando imagen...",
  ...imgProps
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const imgRef = React.useRef<HTMLImageElement | null>(null);

  // Al cambiar la URL original, reiniciamos estados
  useEffect(() => {
    setCurrentSrc(src);
    setAttempt(0);
    setLoaded(false);

    // Si la imagen ya está cargada en caché (complete=true),
    // marcarla como loaded inmediatamente
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const handleError = useCallback(() => {
    if (attempt < maxRetries) {
      setTimeout(() => {
        setAttempt(at => at + 1);
        // anti-caché
        setCurrentSrc(`${src}?_retry=${Date.now()}`);
      }, retryDelay);
    } else {
      console.warn(`No se pudo cargar tras ${maxRetries} intentos: ${src}`);
    }
  }, [attempt, maxRetries, retryDelay, src]);

  const handleLoad = () => {
    setLoaded(true);
  };

  return (
    <>
      {!loaded && (
        <p style={{ margin: 0, paddingTop: "4em", color: "#666", minHeight: "10em", textAlign: "center", verticalAlign: "middle", fontWeight: "bold", minWidth: "300px", maxWidth: "300px" }}>
          {loadingText}
        </p>
      )}
      <img
        ref={imgRef}
        className="file-input-img"
        {...imgProps}
        src={currentSrc}
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        style={{
          display: loaded ? "block" : "none",
          width: "100%",
          height: "auto",
        }}
      />
    </>
  );
};

export default RetryImage;
